/**
 * i18n / nls subsystem contract (见 14 / §5.11): the platform-free types
 * and constants for the localization vocabulary and the cross-process locale
 * surface. The built-in shell catalogs live in `nls.ts`; this file is pure
 * contract so it can be imported anywhere without pulling catalog data.
 *
 * Messages are ICU MessageFormat strings (G11); compilation/formatting happens
 * consumer-side (`@hibro/platform-renderer`, `@hibro/sdk`) so `@hibro/shared` stays
 * zero-dependency.
 */

/** A BCP-47-ish locale tag, e.g. `en-US` / `zh-CN`. */
export type Locale = string;

/**
 * What locale the user wants. An explicit {@link Locale} pins it; `system`
 * follows the OS locale (resolved against the main process's `app.getLocale()`).
 * The `string & {}` keeps the `'system'` literal as an editor hint without the
 * union collapsing to `string` (locales are open tags).
 */
export type LocalePreference = 'system' | (string & {});

/** Fallback locale when nothing else resolves (also the ultimate message fallback). */
export const DEFAULT_LOCALE: Locale = 'en-US';

/**
 * Locales shipped built-in (the initial available set; more can be registered
 * at runtime). Order is the settings-picker order and the same-language peer
 * order used by {@link localeLookupChain}.
 */
export const BUILTIN_LOCALES: readonly Locale[] = [
  'en-US',
  'zh-CN',
  'zh-TW',
  'ru-RU',
  'fr-FR',
  'es-ES',
  'it-IT',
  'pt-BR',
  'vi-VN',
  'hi-IN',
  'fil-PH',
  'id-ID',
  'ja-JP',
  'ko-KR',
  'ms-MY',
  'km-KH',
];

/**
 * Native self-names for built-in locales (settings picker title). Unknown tags
 * stay as the tag itself.
 */
export const LOCALE_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  'en-US': 'English',
  'zh-CN': '简体中文',
  'zh-TW': '繁體中文',
  'ru-RU': 'Русский',
  'fr-FR': 'Français',
  'es-ES': 'Español',
  'it-IT': 'Italiano',
  'pt-BR': 'Português',
  'vi-VN': 'Tiếng Việt',
  'hi-IN': 'हिन्दी',
  'fil-PH': 'Filipino',
  'id-ID': 'Bahasa Indonesia',
  'ja-JP': '日本語',
  'ko-KR': '한국어',
  'ms-MY': 'Bahasa Melayu',
  'km-KH': 'ភាសាខ្មែរ',
};

/** Display label for a locale tag; unknown tags render as the tag itself. */
export function localeDisplayName(locale: Locale): string {
  return LOCALE_DISPLAY_NAMES[locale] ?? locale;
}

/**
 * Intl language tag for a locale, without a region parenthetical.
 * Chinese keeps the script (`zh-Hans` / `zh-Hant`) so simplified and
 * traditional stay distinct. `zh-HK` / `zh-TW` are traditional; bare `zh`
 * and `zh-CN` are simplified.
 */
export function localeLanguageTag(locale: Locale): string {
  const parts = locale.split('-').filter((part) => part.length > 0);
  const language = parts[0]?.toLowerCase() ?? '';
  if (!language) {
    return locale;
  }
  if (language === 'zh') {
    const rest = parts.slice(1);
    const script = rest
      .find((part) => /^[A-Za-z]{4}$/.test(part))
      ?.toLowerCase();
    const region = rest
      .find((part) => /^[A-Za-z]{2}$/.test(part) || /^\d{3}$/.test(part))
      ?.toUpperCase();
    if (
      script === 'hant' ||
      region === 'TW' ||
      region === 'HK' ||
      region === 'MO'
    ) {
      return 'zh-Hant';
    }
    return 'zh-Hans';
  }
  return language;
}

/**
 * The locale's own writing (settings title). Built-in tags use
 * {@link LOCALE_DISPLAY_NAMES}; a regional OS tag shares that name when the
 * language (and, for Chinese, the script) matches. Anything else is the
 * name in that language itself.
 */
export function localeNativeName(locale: Locale): string {
  const exact = LOCALE_DISPLAY_NAMES[locale];
  if (exact) {
    return exact;
  }
  const tag = localeLanguageTag(locale);
  for (const [builtin, name] of Object.entries(LOCALE_DISPLAY_NAMES)) {
    if (localeLanguageTag(builtin) === tag) {
      return name;
    }
  }
  try {
    const name = new Intl.DisplayNames([locale], { type: 'language' }).of(tag);
    if (name && name.toLowerCase() !== tag.toLowerCase()) {
      return name;
    }
  } catch {
    // An unrecognised tag has no DisplayNames entry; the tag itself is the label.
  }
  return locale;
}

/**
 * Language name translated into `uiLocale`. The settings description passes
 * `en-US` so that line stays English. Chinese uses the
 * script tag so the current UI language can say “简体中文” / “Simplified Chinese”
 * rather than a region parenthetical. An unusable `uiLocale` keeps the native name.
 */
export function localeTranslatedName(locale: Locale, uiLocale: Locale): string {
  const tag = localeLanguageTag(locale);
  try {
    const name = new Intl.DisplayNames([uiLocale], { type: 'language' }).of(
      tag,
    );
    if (name && name.toLowerCase() !== tag.toLowerCase()) {
      return name;
    }
  } catch {
    // `uiLocale` is not a tag DisplayNames accepts.
  }
  return localeNativeName(locale);
}

/** Default preference for a fresh install: follow the OS locale. */
export const DEFAULT_LOCALE_PREFERENCE: LocalePreference = 'system';

/**
 * A message catalog: a flat map of message key -> ICU MessageFormat string.
 * Keys are open strings (shell chrome plus namespaced `<moduleId>:<key>`).
 */
export type MessageCatalog = Record<string, string>;

/** Built-in shell catalog (open key set; completeness is a data concern). */
export type ShellCatalog = MessageCatalog;

/**
 * Extra hops for {@link localeLookupChain}, inserted after RFC 4647 truncation
 * and before other same-language peers. Shape matches i18next `fallbackLng`
 * maps. Empty by default — do not pre-seed pairs like `zh-TW → zh-CN`; that
 * hop must come from the generic same-language rule.
 */
export interface LocaleFallbackConfig {
  /** Ultimate fallback language family (`en-US` / `en`). */
  defaultLocale: Locale;
  overrides?: Readonly<Record<string, readonly Locale[]>>;
}

/** Primary language subtag (`zh-Hant-TW` → `zh`). */
export function primaryLanguage(locale: Locale): string {
  return locale.split('-')[0]?.toLowerCase() ?? '';
}

/**
 * RFC 4647 Lookup parents, longest first, excluding `locale` itself
 * (`zh-Hant-TW` → `zh-Hant`, `zh`).
 */
export function truncateLocale(locale: Locale): Locale[] {
  const parts = locale.split('-').filter((part) => part.length > 0);
  const parents: Locale[] = [];
  for (let i = parts.length - 1; i >= 1; i -= 1) {
    parents.push(parts.slice(0, i).join('-'));
  }
  return parents;
}

/**
 * Ordered catalog lookup chain for one locale (见 14). Only tags present in
 * `available` are returned. Default algorithm: exact → configured overrides →
 * RFC 4647 truncation (and overrides on each parent) → other same-language
 * available tags → {@link LocaleFallbackConfig.defaultLocale} family.
 */
export function localeLookupChain(
  locale: Locale,
  available: readonly Locale[],
  config: LocaleFallbackConfig = { defaultLocale: DEFAULT_LOCALE },
): Locale[] {
  const availableSet = new Set(available);
  const out: Locale[] = [];
  const add = (tag: Locale): void => {
    if (availableSet.has(tag) && !out.includes(tag)) {
      out.push(tag);
    }
  };
  const addOverrides = (tag: Locale): void => {
    for (const extra of config.overrides?.[tag] ?? []) {
      add(extra);
    }
  };

  add(locale);
  addOverrides(locale);
  for (const parent of truncateLocale(locale)) {
    add(parent);
    addOverrides(parent);
  }

  const language = primaryLanguage(locale);
  if (language) {
    for (const candidate of available) {
      if (primaryLanguage(candidate) === language) {
        add(candidate);
      }
    }
  }

  add(config.defaultLocale);
  addOverrides(config.defaultLocale);
  for (const parent of truncateLocale(config.defaultLocale)) {
    add(parent);
    addOverrides(parent);
  }
  return out;
}

/**
 * Walk {@link localeLookupChain} and return the first defined catalog value.
 * Shared by the host merge and main-process chrome (tray / jump list).
 */
export function lookupCatalogMessage(
  catalogs: Readonly<Record<string, MessageCatalog | undefined>>,
  locale: Locale,
  key: string,
  available: readonly Locale[] = Object.keys(catalogs),
  config?: LocaleFallbackConfig,
): string | undefined {
  for (const tag of localeLookupChain(locale, available, config)) {
    const value = catalogs[tag]?.[key];
    if (value !== undefined) {
      return value;
    }
  }
  return undefined;
}

/**
 * Cross-process contract for the nls service (见 14 / §5.11). Lives
 * single-instance in the extension host (like `IThemeService`) and is consumed
 * by trusted renderers via an auto RPC proxy. Locale changes broadcast on the
 * EventBus as {@link LOCALE_CHANGED_EVENT} so every window — and every sandboxed
 * surface — re-renders with one shared catalog (§4.3).
 */
export const NLS_CHANNEL = 'nls';

/** EventBus event emitted when the effective locale (or its catalog) changes. */
export const LOCALE_CHANGED_EVENT = 'platform.locale.changed';

/**
 * Payload of {@link LOCALE_CHANGED_EVENT}: the preference + resolved locale only.
 * The catalog is deliberately NOT broadcast — it would expose every module's
 * messages to sandboxed iframes on this public event; instead each consumer
 * re-pulls the catalog it is entitled to (the renderer `getCatalog`, a sandboxed
 * caller the gated `getMessages`).
 */
export interface LocaleChange {
  preference: LocalePreference;
  locale: Locale;
}

export interface INlsService {
  /** The current effective locale (preference resolved against the OS locale). */
  getLocale(): Promise<Locale>;
  /** The current user preference (`system` or an explicit locale). */
  getPreference(): Promise<LocalePreference>;
  /**
   * The OS locale pushed by the main process (`app.getLocale()`), independent
   * of the user's preference. `undefined` until that push arrives.
   */
  getSystemLocale(): Promise<Locale | undefined>;
  /** Set + persist the preference, re-resolve, reload catalogs, and broadcast. */
  setPreference(preference: LocalePreference): Promise<void>;
  /** The full active message catalog (shell + all modules) for the renderer. */
  getCatalog(): Promise<MessageCatalog>;
  /**
   * Platform locales currently selectable, aggregated from shell/full language
   * pack sources. A module's own catalogs never expand this platform list.
   */
  getAvailableLocales(): Promise<Locale[]>;
}

/**
 * Resolve a {@link LocalePreference} + the OS locale into the effective
 * {@link Locale} from `available` (见 14). `system` uses `systemLocale`, else
 * the explicit preference. Fallback: exact match → same-language match →
 * {@link DEFAULT_LOCALE}. Shares {@link primaryLanguage} with
 * {@link localeLookupChain}; this function still returns a single tag.
 */
export function resolveLocale(
  preference: LocalePreference,
  systemLocale: Locale | undefined,
  available: readonly Locale[],
): Locale {
  const candidate = (
    preference === 'system' ? (systemLocale ?? DEFAULT_LOCALE) : preference
  ).trim();
  if (available.includes(candidate)) {
    return candidate;
  }
  const language = primaryLanguage(candidate);
  if (language) {
    const sameLanguage = available.find(
      (loc) => primaryLanguage(loc) === language,
    );
    if (sameLanguage) {
      return sameLanguage;
    }
  }
  return available.includes(DEFAULT_LOCALE)
    ? DEFAULT_LOCALE
    : (available[0] ?? DEFAULT_LOCALE);
}
